import type { ContenidoAyuda, ItemAyuda, SeccionAyuda } from "@/ui/AyudaVista";
import { ATAJO_VISOR_ESC, VISOR_CERRAR, VISOR_DESCARGAR } from "./visorArchivo";

/**
 * Ayuda de Guías: la lista (`pages/Guias`), la vista de una guía
 * (`pages/GuiaDetalle`) y el editor (`GuiaRichEditor`, dentro de
 * `GuiaEditorModal`). Antes, el «?» con globito de la lista —que traía
 * también lo del editor— y los tooltips de la barra del editor y de los
 * botones de PDF.
 */

const BORRADOR: ItemAyuda = {
  label: (
    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
      borrador
    </span>
  ),
  text: "La guía todavía no está lista. Las demás (publicadas) son la referencia del equipo.",
};

/** Qué hace cada botón de la barra del editor de guías. Exportada para que la
 *  ventana del editor (`GuiaEditorModal`) la use en su propia Ayuda. */
export const SECCIONES_EDITOR_GUIA: SeccionAyuda[] = [
  {
    titulo: "Qué hace cada botón de la barra",
    items: [
      { label: "B · I · S", text: "Negrita, itálica y tachado." },
      {
        label: "H · • · 1. · ❝",
        text: "Título de sección, lista con viñetas, lista numerada y cita.",
      },
      { label: "⇤ · ↔ · ⇥ · ☰", text: "Alinea el párrafo a la izquierda, al centro, a la derecha o justificado." },
      { label: "🔗", text: "Enlace: pega o escribe una dirección para el texto seleccionado; vacía, quita el enlace." },
      {
        label: "🖼",
        text: "Inserta una imagen de hasta 5 MB. También puedes arrastrarla o pegarla en el texto. Jala sus esquinas para cambiarle el tamaño (se ve la medida mientras jalas).",
      },
      { label: "▶ YouTube", text: "Inserta un video de YouTube donde tengas el cursor." },
      {
        label: "▦",
        text: "Inserta una tabla de 3×3. Con el cursor dentro de la tabla aparecen +col, −col, +fila, −fila y 🗑; jala el borde entre columnas para cambiar su ancho.",
      },
      {
        label: "⧉ (columna de comandos)",
        text: "Pon el cursor en la columna del comando y tócalo: al ver la guía, cada renglón de esa columna trae un botón para copiar el comando. Tócalo otra vez para quitarlo.",
      },
      {
        label: "+col · −col · +fila · −fila · 🗑",
        text: "Agregan una columna a la derecha o una fila abajo, o quitan la columna, la fila o la tabla entera. Quitar te pide confirmar y se deshace con Ctrl+Z.",
      },
      { label: "―", text: "Inserta una línea divisoria." },
      {
        label: "⤓",
        text: "Salto de página del PDF: lo que sigue empieza en una hoja nueva. En la página no se ve.",
      },
      {
        label: "⤢ 1 hoja",
        text: "Selecciona una sección y tócalo: en el PDF se encoge (letra e imágenes) para caber completa en una sola hoja.",
      },
      {
        label: "▭ PDF horizontal / ▯ PDF vertical",
        text: "Cómo sale la hoja del PDF de esta guía: horizontal para tablas anchas, vertical para texto y capturas. Se guarda con la guía.",
      },
      {
        label: "↑ .md",
        text: "Importa un archivo Markdown (.md, hasta 500 KB) donde tengas el cursor. También puedes arrastrarlo al texto.",
      },
      { label: "⌫", text: "Quita el formato del texto seleccionado." },
    ],
  },
  {
    titulo: "Editor: atajos de teclado",
    items: [
      { label: "Ctrl + B / Ctrl + I", text: "Negrita / itálica." },
      { label: "Ctrl + Z", text: "Deshace el último cambio (también si quitaste una fila, columna o tabla)." },
    ],
  },
];

export function ayudaGuias({
  puedeCrear,
  admin,
}: {
  puedeCrear: boolean;
  admin: boolean;
}): ContenidoAyuda {
  return {
    manual: "guias",
    intro: (
      <p>
        La base de conocimiento del equipo: procedimientos, pruebas y procesos, con texto,
        imágenes, tablas y videos de YouTube. Cada guía se puede ver o descargar en PDF con el
        diseño de Emprenet.
      </p>
    ),
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          ...(puedeCrear
            ? [
                {
                  label: "+ Nueva guía",
                  text: "Abre el editor para escribir una guía nueva. El editor tiene su propio botón «Ayuda» con lo que hace cada botón de su barra.",
                },
              ]
            : []),
          ...(admin
            ? [
                {
                  label: "↻ Queclink",
                  text: "Trae lo nuevo de la base de conocimiento de Queclink. Ya corre sola una vez al día; esto es para no esperar. Las guías nuevas llegan en borrador; las que nadie ha editado aquí se actualizan solas, y si alguien ya editó una, la versión nueva llega aparte en borrador (la nuestra no se pisa).",
                },
              ]
            : []),
          { label: "Buscador", text: "Busca en el título, los tags y el contenido de las guías." },
          {
            label: "Fichas de tag (Filtrar)",
            text: "Toca una para ver solo las guías con ese tag; el número es cuántas lo tienen. Con varias elegidas salen solo las guías que tienen TODAS. «limpiar filtros» las quita.",
          },
          { label: "Una guía de la lista", text: "Tócala para leerla." },
        ],
      },
      { titulo: "Qué significa cada ficha", items: [BORRADOR] },
    ],
  };
}

export function ayudaGuiaDetalle({ puedeEditar }: { puedeEditar: boolean }): ContenidoAyuda {
  return {
    manual: "guias",
    secciones: [
      {
        titulo: "Qué hace cada botón",
        items: [
          { label: "👁 Ver PDF", text: "Abre el PDF aquí mismo, sin descargarlo: sirve para revisar cómo se ve antes de compartirlo." },
          {
            label: "↓ PDF",
            text: "Descarga la guía en PDF con el diseño de Emprenet (encabezado con logo, marca de agua y pie de página numerado), para compartirla con un cliente.",
          },
          VISOR_DESCARGAR,
          VISOR_CERRAR,
          ...(puedeEditar
            ? [
                {
                  label: "Editar",
                  text: "Abre el editor de la guía. Tiene su propio botón «Ayuda» con lo que hace cada botón de su barra.",
                },
                {
                  label: "Eliminar",
                  text: "Borra la guía para siempre (te pide confirmar). No se deshace.",
                },
              ]
            : []),
          {
            label: "⧉ en una tabla",
            text: "Copia el comando de ese renglón al portapapeles.",
          },
          {
            label: "⧉ copiar las N",
            text: "Copia de un jalón todos los comandos o ligas de esa columna, cada uno con el nombre de su renglón, listos para pegar en un correo.",
          },
          { label: "Índice de la guía", text: "Tocar un título del índice te lleva a esa sección." },
          { label: "Flecha ↑ (abajo a la derecha)", text: "Regresa al principio de la guía. Sale al bajar." },
        ],
      },
      { titulo: "Qué significa cada ficha", items: [BORRADOR] },
      { titulo: "Atajos de teclado", items: [ATAJO_VISOR_ESC] },
    ],
  };
}
